/**
 * Nén ảnh ngay trên trình duyệt trước khi tải lên.
 *
 * Hệ thống cũ luôn vẽ lại ảnh lên canvas, giới hạn 800×600 giữ tỉ lệ, rồi xuất
 * WebP chất lượng 0.8 (firebaseStorageService.js). Bản mới tải thẳng tệp gốc —
 * ảnh chụp bằng điện thoại 4–8 MB thay vì ~60 KB. Hậu quả không chỉ là tốn dung
 * lượng: nhân viên mẫu chụp ảnh bằng 4G ở xưởng, và mỗi ảnh nặng gấp hàng chục
 * lần là mỗi lần lưu lâu gấp hàng chục lần — chưa kể mọi lần MỞ chứng từ sau
 * này đều tải lại đúng những ảnh đó.
 *
 * Khác bản cũ ở hai điểm cố ý:
 *
 * 1. GIỚI HẠN 1600×1600 chứ không phải 800×600. Ảnh mẫu nút áo cần nhìn rõ
 *    đường chỉ và vân bề mặt; 800×600 của bản cũ là quá nhỏ cho việc duyệt mẫu
 *    trên màn hình ngày nay. 1600 vẫn nhỏ hơn ảnh gốc hàng chục lần.
 *
 * 2. KHÔNG NÉN NẾU KHÔNG LỢI. Nếu bản nén không nhỏ hơn bản gốc (ảnh đã tối ưu
 *    sẵn, hoặc ảnh nhỏ), giữ nguyên tệp gốc. Nén lại một ảnh đã nén chỉ làm mờ
 *    thêm mà không tiết kiệm gì.
 */

/**
 * Những tệp CHÍNH HÀM NÀY đã tạo ra.
 *
 * Ô đính kèm cần nén sớm để xét ngưỡng dung lượng trên bản đã nén, rồi
 * `uploadFile` lại nén lần nữa. Nén lại một ảnh đã nén thì hoặc vô ích, hoặc
 * mờ thêm một lần nữa. WeakSet nên không giữ tệp lại trong bộ nhớ.
 */
const produced = new WeakSet<File>();

const MAX_EDGE = 1600;
const QUALITY = 0.82;

/** GIF có thể là ảnh động — vẽ lên canvas chỉ giữ được khung đầu tiên. */
const SKIP = new Set(["image/gif"]);

async function decode(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    // Thu hồi ở nhịp sau: thu hồi ngay có thể huỷ ảnh trước khi canvas vẽ xong.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export async function compressImage(file: File): Promise<File> {
  if (produced.has(file)) return file;
  if (!file.type.startsWith("image/") || SKIP.has(file.type)) return file;
  try {
    const img = await decode(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    // Ảnh nút áo thường có nền trắng; PNG trong suốt chuyển sang WebP mà không
    // lót nền sẽ ra nền đen khi in.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + ".webp";
    const out = new File([blob], name, { type: "image/webp", lastModified: file.lastModified });
    produced.add(out);
    return out;
  } catch {
    // Trình duyệt không giải mã được (định dạng lạ, ảnh hỏng) thì tải nguyên
    // bản: máy chủ vẫn có allowlist và giới hạn dung lượng riêng.
    return file;
  }
}
